import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isTyping } from '../game/typing.js';
import {
  markTutorialSeen,
  TUTORIAL_STEPS,
  tutorialSeen,
  useOnboardingStore,
  type TutorialStep,
} from '../state/onboarding.js';
import { useSessionStore } from '../state/session-store.js';
import { useSettingsStore } from '../state/settings.js';

/** Pause after the player does a step, so they see it was noticed. */
const ADVANCE_DELAY_MS = 600;
const ARROW_KEY_CODES = [37, 38, 39, 40];

/**
 * The 30-second controls tutorial (spec §14), shown once after the first entry to the office and
 * again from Settings. Each step finishes when the player does it; it never blocks the game.
 */
export function ControlsTutorial() {
  const { t } = useTranslation();
  const userId = useSessionStore((s) => s.user?.id);
  const step = useOnboardingStore((s) => s.tutorial);
  const keys = useSettingsStore((s) => s.keys);
  const started = useRef(false);

  useEffect(() => {
    if (userId && !tutorialSeen(userId)) useOnboardingStore.getState().startTutorial();
  }, [userId]);

  // Finished or skipped: remember it, so it does not come back on the next visit.
  useEffect(() => {
    if (step) started.current = true;
    else if (started.current && userId) markTutorialSeen(userId);
  }, [step, userId]);

  useEffect(() => {
    if (!step) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const done = (from: TutorialStep) => {
      if (timer) return;
      timer = setTimeout(() => useOnboardingStore.getState().advance(from), ADVANCE_DELAY_MS);
    };
    const moveCodes = [keys.up, keys.down, keys.left, keys.right].map((k) => k.keyCode);
    const onKey = (event: KeyboardEvent) => {
      if (step === 'chat' && event.key === 'Enter') return done('chat');
      if (isTyping()) return;
      if (step === 'move' && [...moveCodes, ...ARROW_KEY_CODES].includes(event.keyCode))
        done('move');
      if (step === 'interact' && event.keyCode === keys.interact.keyCode) done('interact');
    };
    const onPointer = (event: PointerEvent) => {
      const onWorld = event.target instanceof Element && event.target.closest('.game-canvas');
      if (step === 'move' && onWorld) done('move');
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [step, keys]);

  if (!step) return null;
  const index = TUTORIAL_STEPS.indexOf(step);
  const last = index === TUTORIAL_STEPS.length - 1;
  const { advance, closeTutorial } = useOnboardingStore.getState();
  const move = [keys.up, keys.left, keys.down, keys.right].map((k) => k.label).join(' ');

  return (
    <section className="panel tutorial" aria-labelledby="tutorial-title" aria-live="polite">
      <p className="muted small">
        {t('onboarding.tutorial.progress', { step: index + 1, total: TUTORIAL_STEPS.length })}
      </p>
      <h2 id="tutorial-title">{t(`onboarding.tutorial.${step}.title`)}</h2>
      <p>
        {t(`onboarding.tutorial.${step}.body`, {
          move,
          sprint: keys.sprint.label,
          interact: keys.interact.label,
        })}
      </p>
      <div className="tutorial-actions">
        <button type="button" className="secondary" onClick={closeTutorial}>
          {t('onboarding.tutorial.skip')}
        </button>
        <button type="button" onClick={() => advance(step)}>
          {last ? t('onboarding.tutorial.done') : t('onboarding.tutorial.next')}
        </button>
      </div>
    </section>
  );
}
